import { useState } from 'react';
import { useAuth } from '@getmocha/users-service/react';
import { useAuthStore } from '@/shared/stores/authStore';
import { Shield, CheckCircle, Eye, EyeOff, Mail, Lock, AlertCircle } from 'lucide-react';
import { useTheme } from '@/react-app/contexts/ThemeContext';
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
// import GoogleLoginButton from "../components/GoogleLogin";
import tradeLogo from "../../assets/trade-logo.jpeg";



export default function Login() {
    const { isPending } = useAuth();
    const { theme } = useTheme();
    const isDark = theme === 'dark';
    const [authMethod, setAuthMethod] = useState<'google' | 'email'>('google');
    const [showPassword, setShowPassword] = useState(false);
    const navigate = useNavigate();

    const [formData, setFormData] = useState({
        email: '',
        password: '',
    });

    // Access Auth Store properties
    const {
        login: authStoreLogin,
        isLoading: authStoreLoading,
        error: authStoreError,
        clearError: authStoreClearError
    } = useAuthStore();

    const handleEmailLogin = async (e: React.FormEvent<HTMLFormElement>) => {
        try {
            // Prevent default form submission immediately
            e.preventDefault();
            e.stopPropagation();

            // Prevent if already loading
            if (authStoreLoading) {
                return false;
            }

            authStoreClearError(); // Clear any previous errors from the store

            // Client-side validation with user feedback
            if (!formData.email.trim() || !formData.password.trim()) {
                console.error('Email and password are required');
                toast.error("Email and password are required");
                return false;
            }

            // Use the login action from useAuthStore
            const success = await authStoreLogin(formData.email, formData.password);

            if (success) {
                // Use React Router navigation instead of window.location
                toast.success("Login successful 🎉");
                setTimeout(() => navigate("/dashboard"), 1500);
            } else {
                toast.error(authStoreError);
            }

            return false; // Always return false to prevent any default submission
        } catch (error: any) {
            console.error('Login submission error:', error);
            toast.error(authStoreError);
            return false;
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFormData(prev => ({
            ...prev,
            [e.target.name]: e.target.value
        }));
    };

    if (isPending) {
        return (
            <div className={`min-h-screen ${isDark ? 'bg-gray-900' : 'bg-gradient-to-br from-blue-50 via-white to-purple-50'} flex items-center justify-center`}>
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className={isDark ? 'text-gray-300' : 'text-gray-600'}>Loading...</p>
                </div>
            </div>
        );
    }

    return (
        <div className={`min-h-screen ${isDark ? 'bg-gray-900' : 'bg-gradient-to-br from-blue-50 via-white to-purple-50'} flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8`}>
            <div className="max-w-6xl w-full">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">

                    {/* Left Side - Branding & Info */}
                    <div className="space-y-8">
                        <div>
                            <a href="/" className="flex items-center space-x-3 mb-8">
                                <img src={tradeLogo} alt="logo" className="h-10" />
                                <span className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Trade Enrich</span>
                            </a>

                            <h1 className={`text-4xl lg:text-5xl font-bold ${isDark ? 'text-white' : 'text-gray-900'} leading-tight`}>
                                Welcome Back to Your
                                <span className="gradient-text block">Wealth Building Journey</span>
                            </h1>
                            <p className={`text-xl ${isDark ? 'text-gray-300' : 'text-gray-600'} mt-4`}>
                                Continue growing your savings with consistent monthly returns and secure your financial future.
                            </p>
                        </div>

                        <div className="space-y-4">
                            <div className="flex items-center space-x-3">
                                <CheckCircle className="h-6 w-6 text-green-500" />
                                <span className={isDark ? 'text-gray-300' : 'text-gray-700'}>Multiple secure authentication options</span>
                            </div>
                            <div className="flex items-center space-x-3">
                                <CheckCircle className="h-6 w-6 text-green-500" />
                                <span className={isDark ? 'text-gray-300' : 'text-gray-700'}>Bank-level security and encryption</span>
                            </div>
                            <div className="flex items-center space-x-3">
                                <CheckCircle className="h-6 w-6 text-green-500" />
                                <span className={isDark ? 'text-gray-300' : 'text-gray-700'}>Access to all your savings data</span>
                            </div>
                        </div>

                        <div className={`${isDark ? 'bg-gray-800/60' : 'bg-white/60'} backdrop-blur-sm rounded-2xl p-6 border ${isDark ? 'border-gray-700/20' : 'border-white/20'}`}>
                            <div className="grid grid-cols-3 gap-4 text-center">
                                <div>
                                    <div className="text-2xl font-bold text-blue-600">5,000+</div>
                                    <div className={`${isDark ? 'text-gray-400' : 'text-gray-600'} text-sm`}>Active Savers</div>
                                </div>
                                <div>
                                    <div className="text-2xl font-bold text-green-600">$2M+</div>
                                    <div className={`${isDark ? 'text-gray-400' : 'text-gray-600'} text-sm`}>Total Saved</div>
                                </div>
                                <div>
                                    <div className="text-2xl font-bold text-purple-600">10%</div>
                                    <div className={`${isDark ? 'text-gray-400' : 'text-gray-600'} text-sm`}>Max Returns</div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Right Side - Login Form */}
                    <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} rounded-3xl shadow-2xl p-8 lg:p-12`}>
                        <div className="text-center mb-8">
                            <h2 className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'} mb-2`}>Sign In</h2>
                            <p className={isDark ? 'text-gray-300' : 'text-gray-600'}>Access your savings dashboard</p>
                        </div>

                        {/* Auth Method Toggle */}
                        <div className={`flex rounded-xl p-1 mb-6 ${isDark ? 'bg-gray-700' : 'bg-gray-100'}`}>
                            <button
                                onClick={() => setAuthMethod('google')}
                                className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-all duration-200 ${authMethod === 'google'
                                    ? 'bg-blue-600 text-white shadow-md'
                                    : isDark ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-900'
                                    }`}
                            >
                                Google OAuth
                            </button>
                            <button
                                onClick={() => setAuthMethod('email')}
                                className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-all duration-200 ${authMethod === 'email'
                                    ? 'bg-blue-600 text-white shadow-md'
                                    : isDark ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-900'
                                    }`}
                            >
                                Email & Password
                            </button>
                        </div>

                        {/* Google Sign In */}
                        {authMethod === 'google' && (
                            <>
                                {/* <GoogleLoginButton /> */}
                                <p>Coming soon</p>

                                <div className="relative mb-6">
                                    <div className="absolute inset-0 flex items-center">
                                        <div className={`w-full border-t ${isDark ? 'border-gray-600' : 'border-gray-300'}`}></div>
                                    </div>
                                    <div className="relative flex justify-center text-sm">
                                        <span className={`px-4 ${isDark ? 'bg-gray-800 text-gray-400' : 'bg-white text-gray-500'}`}>Secure and Simple</span>
                                    </div>
                                </div>
                            </>
                        )}

                        {/* Email/Password Sign In */}
                        {authMethod === 'email' && (
                            <>
                                <form onSubmit={handleEmailLogin} className="space-y-6 mb-6" noValidate>
                                    {authStoreError && ( // Use authStoreError here
                                        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center space-x-2">
                                            <AlertCircle className="h-5 w-5 text-red-500" />
                                            <span className="text-red-700 text-sm">{authStoreError}</span>
                                        </div>
                                    )}

                                    <div>
                                        <label htmlFor="email" className={`block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                                            Email Address
                                        </label>
                                        <div className="relative">
                                            <Mail className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 ${isDark ? 'text-gray-400' : 'text-gray-400'}`} />
                                            <input
                                                type="email"
                                                id="email"
                                                name="email"
                                                required
                                                value={formData.email}
                                                onChange={handleInputChange}
                                                className={`w-full pl-10 pr-4 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${isDark
                                                    ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                                                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                                                    }`}
                                                placeholder="Enter your email"
                                            />
                                        </div>
                                    </div>

                                    <div>
                                        <label htmlFor="password" className={`block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                                            Password
                                        </label>
                                        <div className="relative">
                                            <Lock className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 ${isDark ? 'text-gray-400' : 'text-gray-400'}`} />
                                            <input
                                                type={showPassword ? 'text' : 'password'}
                                                id="password"
                                                name="password"
                                                required
                                                value={formData.password}
                                                onChange={handleInputChange}
                                                className={`w-full pl-10 pr-12 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${isDark
                                                    ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                                                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                                                    }`}
                                                placeholder="Enter your password"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowPassword(!showPassword)}
                                                className={`absolute right-3 top-1/2 transform -translate-y-1/2 ${isDark ? 'text-gray-400 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'}`}
                                            >
                                                {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                                            </button>
                                        </div>
                                    </div>

                                    <div className="flex items-center justify-between">
                                        <label className="flex items-center">
                                            <input type="checkbox" className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                                            <span className={`ml-2 text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Remember me</span>
                                        </label>
                                        <a href="/forgot-password" className="text-sm text-blue-600 hover:text-blue-700 transition-colors">
                                            Forgot password?
                                        </a>
                                    </div>

                                    <button
                                        type="submit"
                                        disabled={authStoreLoading || !formData.email.trim() || !formData.password.trim()}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            handleEmailLogin(e as any);
                                        }}
                                        className="w-full bg-blue-600 text-white px-6 py-4 rounded-xl font-semibold hover:bg-blue-700 transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {authStoreLoading ? 'Signing In...' : 'Sign In'}
                                    </button>
                                </form>

                                <div className="relative mb-6">
                                    <div className="absolute inset-0 flex items-center">
                                        <div className={`w-full border-t ${isDark ? 'border-gray-600' : 'border-gray-300'}`}></div>
                                    </div>
                                    <div className="relative flex justify-center text-sm">
                                        <span className={`px-4 ${isDark ? 'bg-gray-800 text-gray-400' : 'bg-white text-gray-500'}`}>Or continue with</span>
                                    </div>
                                </div>

                                {/* <GoogleLoginButton /> */}
                            </>
                        )}


                        {/* Security Notice */}
                        <div className={`${isDark ? 'bg-gray-700' : 'bg-gray-50'} rounded-xl p-4 mb-6`}>
                            <h4 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'} mb-2 flex items-center`}>
                                <Shield className="h-4 w-4 mr-2" />
                                Your Security Matters
                            </h4>
                            <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                                Choose between Google OAuth or traditional email login. All credentials are encrypted
                                with bank-level security and your data is protected with industry-standard protocols.
                            </p>
                        </div>

                        {/* Footer Links */}
                        <div className="text-center space-y-4">
                            <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                                Don't have an account?{' '}
                                <a href="/register" className="text-blue-600 hover:text-blue-700 font-medium transition-colors">
                                    Create one here
                                </a>
                            </p>

                            <div className="flex justify-center space-x-6 text-sm">
                                <a href="/about" className={`${isDark ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'} transition-colors`}>
                                    About Us
                                </a>
                                <a href="/contact" className={`${isDark ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'} transition-colors`}>
                                    Help Center
                                </a>
                                <a href="/" className={`${isDark ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'} transition-colors`}>
                                    Back to Home
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
